import { useState, useMemo } from "react";
import { calculateLMI } from "../_data/lmiRates";
import { getStateConfig } from "../_data/stampDutyRates";
import type { AustralianState } from "../_types/calculator.types";

export interface PropertyBudgetInputs {
  // Savings
  totalSavings: number;
  keepInReserve: number;

  // Income for borrowing
  grossIncome: number;
  partnerIncome: number;

  // Loan preferences
  preferredLVR: number; // 80, 85, 90, 95
  interestRate: number;
  loanTerm: number;

  // Location
  state: AustralianState;
  isFirstHomeBuyer: boolean;
}

export interface PropertyBudgetResults {
  // Budget breakdown
  availableDeposit: number;
  estimatedBorrowingPower: number;
  maxPurchasePrice: number;

  // Costs at max price
  stampDuty: number;
  lmiCost: number;
  otherCosts: number;
  totalUpfrontCosts: number;

  // Loan details
  loanAmount: number;
  monthlyRepayment: number;
  effectiveLVR: number;

  // Affordability check
  isAffordable: boolean;
  recommendedPrice: number;
}

const defaultInputs: PropertyBudgetInputs = {
  totalSavings: 100000,
  keepInReserve: 10000,
  grossIncome: 100000,
  partnerIncome: 0,
  preferredLVR: 80,
  interestRate: 6.0,
  loanTerm: 30,
  state: "NSW",
  isFirstHomeBuyer: false,
};

/**
 * Estimate borrowing power (simplified)
 */
function estimateBorrowingPower(
  grossIncome: number,
  partnerIncome: number,
  interestRate: number,
  _loanTerm: number
): number {
  const totalIncome = grossIncome + partnerIncome;

  // Simplified calculation: approximately 5-6x income depending on rate
  // With 3% buffer applied
  const assessmentRate = interestRate + 3;
  const multiplier = assessmentRate < 8 ? 6 : assessmentRate < 9 ? 5.5 : 5;

  return totalIncome * multiplier;
}

/**
 * Calculate stamp duty (simplified)
 */
function calculateStampDuty(
  propertyValue: number,
  state: AustralianState,
  isFirstHomeBuyer: boolean
): number {
  const config = getStateConfig(state);
  let duty = 0;

  for (const tier of config.rates) {
    if (propertyValue >= tier.min && (tier.max === null || propertyValue <= tier.max)) {
      const excess = propertyValue - tier.min;
      duty = tier.base + excess * tier.rate;
      break;
    }
  }

  // Apply FHB concession if applicable (simplified)
  if (isFirstHomeBuyer && propertyValue <= config.fhbConcession.exemptionThreshold) {
    duty = 0;
  } else if (
    isFirstHomeBuyer &&
    propertyValue <= config.fhbConcession.concessionThreshold
  ) {
    duty = duty * 0.5; // Simplified partial concession
  }

  return duty;
}

/**
 * Calculate monthly repayment
 */
function calculateMonthlyPayment(principal: number, annualRate: number, years: number): number {
  const monthlyRate = annualRate / 100 / 12;
  const periods = years * 12;

  if (monthlyRate === 0) return principal / periods;

  return (
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, periods))) /
    (Math.pow(1 + monthlyRate, periods) - 1)
  );
}

/**
 * Property Budget Calculator Hook
 */
export function usePropertyBudgetCalculator() {
  const [inputs, setInputs] = useState<PropertyBudgetInputs>(defaultInputs);

  const results = useMemo<PropertyBudgetResults>(() => {
    const {
      totalSavings,
      keepInReserve,
      grossIncome,
      partnerIncome,
      preferredLVR,
      interestRate,
      loanTerm,
      state,
      isFirstHomeBuyer,
    } = inputs;

    // Available deposit
    const availableDeposit = totalSavings - keepInReserve;

    // Estimate borrowing power
    const estimatedBorrowingPower = estimateBorrowingPower(
      grossIncome,
      partnerIncome,
      interestRate,
      loanTerm
    );

    // Calculate max purchase price iteratively
    // Start with deposit / (1 - LVR) as initial estimate
    const depositRatio = 1 - preferredLVR / 100;
    let maxPurchasePrice = availableDeposit / depositRatio;

    // Iterate to account for stamp duty and LMI
    for (let i = 0; i < 5; i++) {
      const stampDuty = calculateStampDuty(maxPurchasePrice, state, isFirstHomeBuyer);
      const loanAmount = maxPurchasePrice - availableDeposit + stampDuty;
      const lmiCost = preferredLVR > 80 ? calculateLMI(loanAmount, maxPurchasePrice) : 0;
      const otherCosts = 3000; // Legal, inspections, etc.

      const totalCosts = stampDuty + lmiCost + otherCosts;
      const effectiveDeposit = availableDeposit - totalCosts;

      // Recalculate max price
      maxPurchasePrice = effectiveDeposit / depositRatio;

      // Cap at borrowing power
      const maxLoanAllowed = Math.min(
        maxPurchasePrice * (preferredLVR / 100),
        estimatedBorrowingPower
      );
      maxPurchasePrice = Math.min(maxPurchasePrice, maxLoanAllowed / (preferredLVR / 100));
    }

    // Round down to nearest $10k
    maxPurchasePrice = Math.floor(maxPurchasePrice / 10000) * 10000;

    // Calculate final costs at this price
    const stampDuty = calculateStampDuty(maxPurchasePrice, state, isFirstHomeBuyer);
    const loanAmount = maxPurchasePrice * (preferredLVR / 100);
    const lmiCost = preferredLVR > 80 ? calculateLMI(loanAmount, maxPurchasePrice) : 0;
    const otherCosts = 3000;
    const totalUpfrontCosts = stampDuty + lmiCost + otherCosts;

    // Calculate monthly repayment
    const monthlyRepayment = calculateMonthlyPayment(loanAmount + lmiCost, interestRate, loanTerm);

    // Effective LVR (including LMI)
    const effectiveLVR = ((loanAmount + lmiCost) / maxPurchasePrice) * 100;

    // Affordability check (repayment should be < 30% of gross monthly income)
    const monthlyIncome = (grossIncome + partnerIncome) / 12;
    const isAffordable = monthlyRepayment < monthlyIncome * 0.3;

    // Recommended price if not affordable
    const recommendedPrice = isAffordable
      ? maxPurchasePrice
      : Math.floor((maxPurchasePrice * 0.85) / 10000) * 10000;

    return {
      availableDeposit: Math.round(availableDeposit),
      estimatedBorrowingPower: Math.round(estimatedBorrowingPower),
      maxPurchasePrice: Math.round(maxPurchasePrice),
      stampDuty: Math.round(stampDuty),
      lmiCost: Math.round(lmiCost),
      otherCosts,
      totalUpfrontCosts: Math.round(totalUpfrontCosts),
      loanAmount: Math.round(loanAmount),
      monthlyRepayment: Math.round(monthlyRepayment),
      effectiveLVR: Math.round(effectiveLVR * 10) / 10,
      isAffordable,
      recommendedPrice: Math.round(recommendedPrice),
    };
  }, [inputs]);

  const updateInput = <K extends keyof PropertyBudgetInputs>(
    key: K,
    value: PropertyBudgetInputs[K]
  ) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  return { inputs, results, updateInput, setInputs };
}
